import React from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, ButtonSize, Box } from 'edenred-ui';
import { FormProvider, useForm } from 'react-hook-form';

import { PasswordField, ConfirmPasswordField } from '@epi-forms/controls';
import { FormFields } from '@epi-forms/helpers';
import { isEmptyObj } from '@epi-helpers/isEmptyObj';
import { getUserName } from '@epi-selectors/profile';
import {
  PasswordValidationContainer,
  PasswordValidation
} from '@epi-shared/components';

import { changePassword as changePasswordLoadingSelector } from '../../selectors/loading';
import { ApiConstants } from '../../constants/actions';
import { required, password } from '../../helpers/formHelpers/validators';
import { ErrorPanel } from '../../components/ErrorPanel/ErrorPanel';
import { changePassword } from '../../actions/profile';

export function ChangePassword() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const username = useSelector(getUserName);
  const sendingRequest = useSelector(changePasswordLoadingSelector);

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      [FormFields.Password]: '',
      [FormFields.NewPassword]: '',
      [FormFields.ConfirmPassword]: ''
    }
  });

  const {
    reset,
    handleSubmit,
    watch,
    formState: { errors, dirtyFields }
  } = form;

  const onSubmit = values => {
    dispatch(changePassword(values));
    setTimeout(() => {
      window.blur();
      reset();
    }, 0); // Run after rendering
  };

  const newPassword = watch(FormFields.NewPassword);
  const isUsernameSameAsPassword = username !== newPassword;

  const isSubmitBtnDisabled =
    [FormFields.NewPassword, FormFields.ConfirmPassword].some(
      field => !dirtyFields[field]
    ) ||
    !isEmptyObj(errors) ||
    sendingRequest ||
    !isUsernameSameAsPassword;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ErrorPanel failedActionName={ApiConstants.CHANGE_PASSWORD} />
        <PasswordField
          name={FormFields.Password}
          label={t('forms.change_password.current_password')}
          validators={[required]}
        />
        <PasswordField
          displayValidationResults={false}
          showIconPassword
          name={FormFields.NewPassword}
          label={t('forms.change_password.new_password')}
          validators={[required, password]}
        />
        <PasswordValidationContainer password={newPassword} />
        <PasswordValidation
          isValid={isUsernameSameAsPassword}
          text={t('validation_messages.password_same_as_username')}
        />
        <ConfirmPasswordField
          label={t('forms.change_password.confirm_new_password')}
          field={FormFields.NewPassword}
        />
        <Box mt={3}>
          <Button
            id="change-password-submit-btn"
            type="submit"
            disabled={isSubmitBtnDisabled}
            className="pull-right"
            size={ButtonSize.Medium}
          >
            {t('forms.change_password.submit')}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );
}
